import { toastInfo, useConfirmationDialog } from '@justomx/atlas-components'
import { useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'

import { shipContainers } from 'client/stock-transfers/stock-transfers.client'
import { useRequiredParams } from 'hooks/params.hook'
import { OrderDetailSchema } from 'types/assembly/model'
import type { ShipmentPreparationPallet } from 'types/shipment/model'
import { ZOD_ERROR } from 'utils/constants'
import { toastError, toastSuccess } from 'utils/toast'
import { useGoToShipment } from '../Shipment.navigator'
import { transferPalletsAtom } from '../Shipment.state'

export const usePreparation = () => {
  const navigate = useNavigate()
  const { transferId, docNum } = useRequiredParams('transferId', 'docNum')
  const [palletsData, setPalletsData] = useState<
    Record<string, ShipmentPreparationPallet>
  >({})
  const { dialogRef, open } = useConfirmationDialog()
  const shipmentPage = useGoToShipment()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pickedFinished, setPickedFinished] = useState(false)
  const [filteredPallets, setFilteredPallets] = useState<
    ShipmentPreparationPallet[]
  >(Object.values([]))
  const setTransferPallets = useSetAtom(transferPalletsAtom)
  const rawOrder = useLoaderData()

  useEffect(() => {
    const result = OrderDetailSchema.safeParse(rawOrder)
    if (!result.success) {
      console.error(result.error)
      throw Error(ZOD_ERROR)
    }

    const palletsMap: Record<string, ShipmentPreparationPallet> = {}
    result.data.containers.forEach((container) => {
      palletsMap[container.id] = {
        container: container.id,
        channel: 'CANAL-1',
        productCount: container.totalSKUs
      }
    })
    setPickedFinished(!!result.data.pickedFinished)
    setPalletsData(palletsMap)
  }, [rawOrder])

  const atLeastOnePalletScanned = useMemo(() => {
    return Object.values(palletsData).some((pallet) => pallet.scanned)
  }, [palletsData])

  const scannedPallets = useMemo(() => {
    return Object.values(palletsData)
      .filter((pallet) => pallet.scanned)
      .map(({ container }) => container)
  }, [palletsData])

  const onGoBack = () => {
    navigate(-1)
  }

  const onScanPallet = (scanned: string) => {
    if (!palletsData[scanned]) {
      toastError(
        'La tarima escaneada no pertenece a la solicitud, intenta con otra.'
      )
      return
    }

    setPalletsData((prev) => ({
      ...prev,
      [scanned]: { ...prev[scanned], scanned: !prev[scanned].scanned }
    }))
    setFilteredPallets((prev) =>
      prev.map((pallet) =>
        pallet.container === scanned
          ? { ...pallet, scanned: !pallet.scanned }
          : pallet
      )
    )
  }

  const onContinue = () => {
    if (!pickedFinished) {
      toastInfo({
        message:
          'No es posible realizar el embarque si el picking no ha finalizado.',
        showIcon: true
      })
      return
    }

    open()
  }

  const onConfirmPallets = async () => {
    try {
      setLoading(true)
      const result = await shipContainers(transferId, scannedPallets)
      console.info(result)
      toastSuccess(
        `Has despachado ${scannedPallets.length} de ${
          Object.values(palletsData).length
        } tarimas de la ${docNum}.`
      )
      shipmentPage.go()
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const openSearchModal = () => {
    setTransferPallets(Object.values(palletsData))
    setIsModalOpen(true)
  }

  const onCloseModal = () => {
    setIsModalOpen(false)
    setFilteredPallets([])
  }

  const filterPallets = (lpn: string) => {
    if (!lpn.length) {
      setFilteredPallets([])
      return
    }

    const lowerCaseSearch = lpn.toLowerCase()
    const filtered = Object.values(palletsData).filter((pallet) =>
      pallet.container.toLowerCase().includes(lowerCaseSearch)
    )
    setFilteredPallets(filtered)
  }

  return {
    docNum,
    palletsData,
    atLeastOnePalletScanned,
    dialogRef,
    scannedPallets,
    isModalOpen,
    filteredPallets,
    loading,
    actions: {
      onGoBack,
      onScanPallet,
      onContinue,
      onConfirmPallets,
      onCloseModal,
      openSearchModal,
      filterPallets
    }
  }
}
